<template>
  <div class="invitation-scene">
    <div class="standart-navigation">
      <div class="row actions">
        <span class="title">{{ $tr('invites.newInvitations') }}</span>

        <actions @show-xls-import-modal="showXlsImportModal = true" />
      </div>
    </div>

    <transition name="modal">
      <xls-import-modal v-if="showXlsImportModal" @close="showXlsImportModal = false" />
    </transition>

    <vpreloader v-if="page_loading || !company.meta || !file_loaded" width="2" size="64" />

    <div
      v-if="page_loading === false && file_loaded && valid_import && opened === false"
      class="show_more"
      @click="opened = true"
    >
      <div class="success_text">{{ $tr('invites.text_success_import') }}</div>
      <vbutton label="invites.text_show_imported_list" color="white" text_color="grey" />
    </div>

    <process-define />

    <invite-list
      v-if="!!company.meta && loaded"
      v-show="page_loading === false && file_loaded && (opened || valid_import === false)"
      :list="invites"
      :query-vacancy="queryVacancy"
      :recruiter-country="userCountry"
      @change-valid-status="changeValidStatus"
    />

    <success-modal v-if="successInvited" />

    <repeated-modal
      v-if="showRepeatedModal"
      :invites="repeatedInvites"
      @close="resetRepeatedInvites"
      @accept="forceInvites"
      @delete-row="deleteRow"
    />

    <sms-modal
      v-if="isSmsModalVisible"
      :loading="loadingSms"
      :model="smsData"
      @close="closeSms"
      @submit="submitSms"
      @request="requestSms"
    />

    <div class="table-footer">
      <send-button
        :label="'invites.sendNewInvitations'"
        :disabled="file_loaded === false || page_loading || invites.length < 2 || has_errors"
        is-emit-click
        @send-invites="sendInvites"
      />
    </div>

    <rates-responses-popup
      :is-show="responsesPopupEnabled && !forceSend"
      show-custom-buttons
      @change="responses_action_change"
    >
      <div slot="buttons">
        <popup-send-button
          label="rates.saveAndSend"
          :company-id="company_id"
          :response-limit-expired-action="response_limit_expired_action"
          :sending-method="sendingMethod"
        />

        <vbutton label="rates.cancel" color="white" @click="hidePopupResponseWarning" />
      </div>
    </rates-responses-popup>

    <div id="dummy" ref="dummy" class="empty"></div>
  </div>
</template>

<script>
  import { mapState, mapGetters, mapActions } from 'vuex';
  import axios from 'axios';
  import Actions from '../Actions';
  import InviteList from '../List';
  import SuccessModal from '../SuccessModal';
  import XlsImportModal from '../XlsImportModal';
  import RatesResponsesPopup from '@/routes_new/inner/settings/rates/RatesResponsesPopup';
  import { getCountryCodeFromPlaceID } from '../CORSrequest.js';
  import ProcessDefine from '../process-define/index';
  import SendButton from '../send-button/index';
  import PopupSendButton from '../popup-send-button/index';
  import RepeatedModal from '../repeated-modal/index';
  import SmsModal from '../SmsModal';

  export default {
    name: 'InvitesCreationScene',

    components: {
      ProcessDefine,
      SendButton,
      PopupSendButton,
      Actions,
      InviteList,
      SuccessModal,
      RepeatedModal,
      XlsImportModal,
      RatesResponsesPopup,
      SmsModal
    },

    beforeRouteLeave(to, from, next) {
      if (this.needAskUser) {
        if (this.canGo()) {
          next();
          window.onbeforeunload = null;
        } else {
          next(false);
        }
      } else {
        next();
        window.onbeforeunload = null;
      }
    },

    data() {
      return {
        loaded: false,
        showXlsImportModal: false,
        showRepeatedModal: false,
        canBeSent: false,
        existingEmails: [],
        userCountry: '',
        response_limit_expired_action: '0',
        opened: false,
        smsBalanceNotificated: false,
        isSmsModalVisible: false,
        loadingSms: false,
        queryVacancy: null
      };
    },

    computed: {
      ...mapGetters('invites/creation', {
        sendingMethod: 'sendingMethod',
        forceSend: 'forceSend',
        canSendSms: 'canSendSms',
        smsData: 'smsData',
        responsesPopupEnabled: 'responsesPopupEnabled',
        file_loaded: 'file_loaded',
        invitesValidStatus: 'invitesValidStatus',
        loading: 'loading',
        repeatedInvites: 'repeatedInvites',
        successInvited: 'successInvited',
        page_loading: 'sending',
        invites: 'list'
      }),

      ...mapGetters('user', { user: 'current' }),

      ...mapGetters('locale', { locale: 'current' }),

      ...mapGetters('company', { company: 'one' }),

      ...mapGetters('company_new', ['isRatesExpired']),

      ...mapState('company_new', ['rates']),

      is_sms_enabled() {
        if (!this.rates) {
          return false;
        }

        return this.rates ? this.rates.is_sms_enabled : false;
      },

      is_sms_unlimited() {
        if (!this.rates) {
          return false;
        }
        return this.rates ? this.rates.is_sms_unlimited : false;
      },

      isShowSmsBalanceNotificate() {
        if (!this.is_sms_enabled || this.is_sms_unlimited) {
          return false;
        }
        return !this.smsBalanceNotificated;
      },

      valid_import() {
        if (this.invites.length > 1 && this.invites.filter(i => i.edited).length > 1) {
          return false;
        }
        if (this.invites.length === 1) {
          return false;
        }
        if (this.invites.length > 1 && this.invites.filter(i => !!i.errors).length > 0) {
          return false;
        }
        return true;
      },

      userPlaceID() {
        const geo = this.user ? this.user.geo : null;

        return geo ? geo.place_id : null;
      },

      company_id() {
        if (!this.company) {
          return null;
        }

        return this.company.id;
      },

      needAskUser() {
        return this.haveEnteredData && !this.successInvited;
      },

      canBeSended() {
        const all = this.invitesValidStatus.length;

        return (
          all > 1 &&
          this.invitesValidStatus
            .filter((s, id) => id !== all - 1)
            .every(s => {
              // if (s.localId && !s.valid) {
              //   console.log('invalid: ', s);
              // }

              return s.valid;
            })
        );
      },

      hasRepeated() {
        return this.repeatedInvites ? this.repeatedInvites.length > 0 : false;
      },

      listRoute() {
        return { name: 'invites' };
      },

      haveEnteredData() {
        return this.invites.some(invite => {
          return !!(
            invite.vacancy.id ||
            invite.candidate.name ||
            invite.candidate.surname ||
            invite.candidate.email ||
            invite.candidate.phone
          );
        });
      },

      has_errors() {
        return this.errors && this.errors.items && this.errors.items.length > 0;
      }
    },

    watch: {
      hasRepeated(val) {
        this.showRepeatedModal = val;
      },

      needAskUser(val) {
        if (val) {
          const message = this.$tr('invites.invite.leaveUnsavedData');
          window.onbeforeunload = function () {
            return message;
          };
        } else {
          window.onbeforeunload = null;
        }
      },

      userPlaceID(val) {
        this.geoRequest(val);
      },

      canSendSms(val) {
        if (!val) {
          this.isSmsModalVisible = true;
        }
      }
    },

    beforeMount() {
      if (this.$route.query.vacancy) {
        this.queryVacancy = this.$route.query.vacancy;
      }
      this.$store.commit('invites/creation/createNewEmptyRow');

      if (this.userPlaceID) {
        this.geoRequest(this.userPlaceID);
      } else {
        // Fetch user info
        if (!this.user || !this.user.id) {
          this.$store.dispatch(
            'user/userInfoRequest',
            this.$store.getters['user/accessToken'].user.email
          );
        }
      }
      this.fetchVacancies();
    },

    mounted() {
      if (this.isRatesExpired) {
        this.$router.push(this.listRoute);
      }
    },

    beforeDestroy() {
      this.$store.dispatch('invites/creation/resetState');
    },

    methods: {
      ...mapActions('vacancies', ['fetchVacanciesListDropdown']),

      sendInvites() {
        this.smsBalanceNotificated = false;
        this.$store.dispatch('invites/creation/sendInvitesCheck', {
          isShowSmsBalanceNotificate: this.isShowSmsBalanceNotificate
        });
      },

      forceInvites() {
        this.$store.dispatch('invites/creation/forceInvitesCheck', {
          isShowSmsBalanceNotificate: this.isShowSmsBalanceNotificate
        });
      },

      geoRequest(placeId) {
        // const placeId = 'ChIJybDUc_xKtUYRTM9XV8zWRD0'
        const locale = this.locale;
        getCountryCodeFromPlaceID(placeId, locale)
          .then(res => {
            this.userCountry = res;
          })
          .finally(() => {
            this.loaded = true;
          });
      },

      setSmsModalVisible(v) {
        this.isSmsModalVisible = v;
      },

      requestSms() {
        this.loadingSms = true;

        axios
          .post('/1.0/company-limits/tariff/request', {
            modules: [
              {
                name: this.$tr('price.popup.sms'),
                id: 'sms'
              }
            ]
          })
          .then(() => {
            this.loadingSms = false;
            this.$noty.success(this.$tr('rates.successRequest'));
            this.closeSms();
          })
          .catch(() => {
            this.$noty.error(this.$tr('common.error.text'));
            this.loadingSms = false;
          });
      },

      submitSms() {
        this.loadingSms = true;
        this.smsBalanceNotificated = true;
        this.$store
          .dispatch('invites/creation/sendInvitesCheck', {
            isShowSmsBalanceNotificate: false
          })
          .then(() => {
            this.loadingSms = false;
            this.setSmsModalVisible(false);
          })
          .catch(() => {
            this.loadingSms = false;
          });
      },

      closeSms() {
        this.setInvitesInitialState();
        this.smsBalanceNotificated = true;
        this.setSmsModalVisible(false);
        this.$store.commit('invites/creation/setCanSendSms', true);
      },

      setInvitesInitialState() {
        this.$store.commit('invites/creation/setSending', false);
        this.$store.commit('invites/creation/set_file_loaded', true);
        this.$store.commit('invites/creation/setIvitesTmp');
        this.opened = false;
      },

      routeToList() {
        this.$router.push(this.listRoute);
      },

      changeValidStatus(val) {
        this.canBeSent = val;
      },

      resetRepeatedInvites() {
        this.$store.commit('invites/creation/setSending', false);
        this.hidePopupResponseWarning();
        this.$store.dispatch('invites/creation/clearRepeatedInvites');
      },

      handleSelectedFiles(files) {
        if (!files[0]) {
          return null;
        }

        this.$store.dispatch('invites/creation/importFromFile', files[0]);
      },

      canGo() {
        return this.needAskUser
          ? window.confirm(this.$tr('invites.invite.leaveUnsavedData'))
          : false;
      },

      deleteRow(localId) {
        // Check for invites with same e-mail
        const invite = this.invites.find(i => Number(i.localId) === Number(localId));
        const email = invite ? invite.candidate.email : null;
        const invitesWithSameEmailLocalIds = email
          ? this.invites
              .filter(i => i.candidate && i.candidate.email === email && i.localId !== localId)
              .map(i => i.localId)
          : [];

        // Clear list from invites with same e-mail
        // (if recruiter send two or more invite to the one e-mail)
        if (invitesWithSameEmailLocalIds.length > 0) {
          invitesWithSameEmailLocalIds.forEach(localId => {
            this.$store.dispatch('invites/creation/deleteRow', localId);
          });
        }

        if (this.invites.length === 1) {
          this.$store.dispatch('invites/creation/clearRow', localId);
        } else {
          this.$store.dispatch('invites/creation/deleteRow', localId);
        }
        this.$store.commit('invites/creation/setSending', false);
      },

      hidePopupResponseWarning() {
        this.$store.commit('invites/creation/setResponsesPopupStatus', false);
      },

      responses_action_change(val) {
        this.response_limit_expired_action = val;
      },

      fetchVacancies() {
        this.fetchVacanciesListDropdown({
          isActive: true,
          permission: 'invite'
        });
      }
    }
  };
</script>

<style lang="scss" scoped>
  @import '../../../../styles/colors.scss';

  span.title {
    font-size: 28px;
  }

  .table-footer {
    margin-top: 20px;
  }

  .standart-navigation {
    padding-bottom: 25px;
  }

  .vcv-button {
    letter-spacing: normal;
    margin: 0 auto;
  }
  .show_more {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 15px;
    margin-bottom: 40px;
  }
  .success_text {
    font-size: 16px;
    // font-family: 'Roboto', sans-serif;
    margin: 0 auto;
    margin-bottom: 10px;
  }
</style>
